import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { Search, Calculator, ShoppingCart, Check, X, ArrowRight, Info } from 'lucide-react';

const MarquagePage = () => {
  const { user } = useAuth();
  const [fabrics, setFabrics] = useState([]);
  const [loadingFabrics, setLoadingFabrics] = useState(true);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [searchFabric, setSearchFabric] = useState('');

  const [form, setForm] = useState({
    text: '',
    hauteur_lambrequin: 25,
    largeur: 300,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [fabRes, priceRes] = await Promise.all([
          axios.get(`${API}/marquage/products`, { params: { limit: 100 } }),
          axios.get(`${API}/marquage/film-pricing`)
        ]);
        setFabrics(fabRes.data || []);
        setPricing(priceRes.data);
      } catch (e) { console.error(e); }
      finally { setLoadingFabrics(false); }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'text' ? value : parseFloat(value) || 0 }));
    setCalcResult(null);
  };

  const handleCalculate = async () => {
    if (!form.text) return;
    setCalcLoading(true);
    setCalcResult(null);
    try {
      const res = await axios.post(`${API}/marquage/calculate`, {
        text: form.text,
        hauteur_lambrequin: form.hauteur_lambrequin,
        largeur: form.largeur,
        product_id: selectedFabric?.id
      });
      setCalcResult(res.data);
    } catch (e) { console.error(e); }
    finally { setCalcLoading(false); }
  };

  const handleOrder = async () => {
    if (!calcResult) return;
    try {
      await axios.post(`${API}/pro/orders`, {
        items: [{
          type: 'marquage',
          product_id: selectedFabric?.id,
          product_name: `Marquage lambrequin: "${form.text}"`,
          details: {
            text: form.text,
            hauteur_lambrequin: form.hauteur_lambrequin,
            largeur: form.largeur,
            toile: selectedFabric ? `${selectedFabric.supplier} - ${selectedFabric.code1} ${selectedFabric.code2}` : 'Non spécifiée',
            ...calcResult
          },
          quantity: 1,
          unit_price: calcResult.prix_film || 0,
          total_price: (calcResult.prix_film || 0) + (calcResult.frais_fixes || 0)
        }]
      }, { withCredentials: true });
      setOrderSuccess(true);
      setTimeout(() => setOrderSuccess(false), 3000);
    } catch (e) { console.error(e); }
  };

  const filteredFabrics = fabrics.filter(f =>
    !searchFabric || `${f.supplier} ${f.code1} ${f.code2} ${f.gamme}`.toLowerCase().includes(searchFabric.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-[#1a1a1a] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
            <Link to="/mon-espace" className="hover:text-orange-400">Espace Pro</Link>
            <span>/</span>
            <span className="text-white">Marquage sur lambrequin</span>
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Marquage / Flocage sur Lambrequin</h1>
          <p className="text-gray-400">Texte + logo sur mesure. Devis instantané. Prix direct usine.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Configurator */}
          <div>
            {/* Film Pricing Info */}
            {pricing && (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3">
                <Info size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <strong>Tarification :</strong> {pricing.price_per_m2} €/m² × coeff. {pricing.ratio} + {pricing.fixed_cost} € de frais fixes.
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
              <h2 className="font-black text-gray-900 text-lg">Configurez votre marquage</h2>

              {/* Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Texte à marquer *</label>
                <input
                  type="text"
                  name="text"
                  value={form.text}
                  onChange={handleChange}
                  placeholder="NOM DE VOTRE ENTREPRISE"
                  maxLength={80}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg font-bold tracking-wide focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all uppercase"
                />
                {form.text && (
                  <div className="mt-3 bg-gray-800 rounded-lg p-4 flex items-center justify-center">
                    <span className="text-white text-xl font-black tracking-widest">{form.text.toUpperCase()}</span>
                  </div>
                )}
              </div>

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Largeur lambrequin (cm)</label>
                  <input
                    type="number"
                    name="largeur"
                    value={form.largeur}
                    onChange={handleChange}
                    min="50" max="600"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Hauteur lambrequin (cm)</label>
                  <input
                    type="number"
                    name="hauteur_lambrequin"
                    value={form.hauteur_lambrequin}
                    onChange={handleChange}
                    min="10" max="80"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-orange-400 transition-all"
                  />
                </div>
              </div>

              <button
                onClick={handleCalculate}
                disabled={!form.text || calcLoading}
                className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white font-bold py-3.5 rounded-xl transition-all"
              >
                <Calculator size={18} />
                {calcLoading ? 'Calcul en cours...' : 'Calculer le devis'}
              </button>

              {/* Result */}
              {calcResult && (
                <div className="bg-orange-50 rounded-xl border border-orange-100 p-5">
                  <h3 className="font-black text-gray-900 mb-4">Détail du devis</h3>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hauteur texte</span>
                      <span className="font-semibold">{calcResult.hauteur_texte_cm} cm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Largeur texte</span>
                      <span className="font-semibold">{calcResult.largeur_texte_cm} cm</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surface film</span>
                      <span className="font-semibold">{calcResult.surface_film_m2} m²</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prix film</span>
                      <span className="font-semibold">{calcResult.prix_film?.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Frais fixes</span>
                      <span className="font-semibold">{calcResult.frais_fixes?.toFixed(2)} €</span>
                    </div>
                  </div>
                  <div className="border-t border-orange-200 pt-4 flex items-center justify-between">
                    <span className="font-bold text-gray-900">Total HT</span>
                    <span className="text-2xl font-black text-orange-600">{((calcResult.prix_film || 0) + (calcResult.frais_fixes || 0)).toFixed(2)} €</span>
                  </div>

                  {orderSuccess ? (
                    <div className="mt-4 flex items-center gap-2 text-green-600 font-semibold">
                      <Check size={18} /> Commande enregistrée avec succès !
                    </div>
                  ) : (
                    <button
                      onClick={handleOrder}
                      disabled={user?.status !== 'approved'}
                      className="mt-4 w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-all"
                    >
                      <ShoppingCart size={16} />
                      {user?.status !== 'approved' ? 'Compte en attente' : 'Passer commande'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Toile selection */}
          <div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-black text-gray-900 mb-1">Choisir la toile (optionnel)</h2>
              <p className="text-gray-400 text-sm mb-4">Sélectionnez la toile sur laquelle vous souhaitez le marquage.</p>

              <div className="relative mb-4">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une toile..."
                  value={searchFabric}
                  onChange={e => setSearchFabric(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                />
              </div>

              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {loadingFabrics ? (
                  Array(6).fill(0).map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />)
                ) : filteredFabrics.slice(0, 50).map(f => {
                  const imgUrl = f.thumbnail?.startsWith('http') ? f.thumbnail : null;
                  return (
                    <div
                      key={f.id}
                      onClick={() => setSelectedFabric(selectedFabric?.id === f.id ? null : f)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedFabric?.id === f.id ? 'bg-orange-50 border border-orange-300' : 'border border-gray-100 hover:border-orange-200 hover:bg-orange-50'
                      }`}
                    >
                      {imgUrl ? (
                        <img src={imgUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm truncate">{f.code1} {f.code2}</p>
                        <p className="text-gray-400 text-xs truncate">{f.gamme}</p>
                      </div>
                      {selectedFabric?.id === f.id && (
                        <Check size={16} className="text-orange-500 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarquagePage;
